import { getLogger } from '@logtape/logtape';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { ApiError, api } from '@/lib/api-client.ts';

import { EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { RequiredForms } from '@/models/forms/RequiredForm.ts';
import {
    FormDataAppointmentDto,
    FormKeyObject as FormTemplateAppointment,
} from '@/models/forms/backend-types/FormDataAppointment.ts';
import { FormDataCode, FormDataCodeDto, FormDataCodeResult } from '@/models/forms/backend-types/FormDataCode.ts';
import { FormDataRequiredTemplate } from '@/models/forms/backend-types/FormDataRequiredTemplates.ts';
import { GetDocumentsResult } from '@/models/forms/backend-types/GetDocumentsResult.ts';
import { FormTemplate } from '@/models/forms/backend-types/form-template/FormTemplate.ts';
import { FormsFetch, FormsFetchStatus, FormsResolution } from '@/models/forms/forms-fetch.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { getCustomerId } from '@/utils/customer-utils.ts';
import { formatRequiredForms, getFormsFetchData, getFormsResolution, getFormsSkipReason } from '@/utils/formUtils.ts';
import { getInsuranceTypeFromAppointment } from '@/utils/insurance-utils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';
import { getUnregisteredUserId } from '@/utils/patientUtils.ts';
import { joinAppointmentServicesKeys } from '@/utils/practitioner/cachedDataUtils.ts';

const logger = getLogger(['service', 'form-data']);

interface RequiredFormsResponse {
    templates: FormDataRequiredTemplate[];
}

interface SelectedFormsResponse {
    data: { forms: FormTemplateAppointment[] };
}

interface SharedFormsResponse {
    data: GetDocumentsResult[];
}

/**
 * Neither mandatory lookup has a rejection meaning "nothing stored yet" — they answer `200` or `400` — so
 * no rejection here may be read as an empty answer. The lookups that do `404` for absent data are the
 * selected-forms and code ones, whose callers already treat a missing record as none.
 */
const toFormsFetchFailure = <T>(error: unknown, message: string): FormsFetch<T[]> => {
    logger.debug(message, serializeApiError(error));

    return { status: FormsFetchStatus.FAILED, http_status: error instanceof ApiError ? error.status : 0 };
};

const getRequiredFormsForAppointment = async (
    userId: string,
    customerId: string,
    appointment: DailyAppointment,
    insuranceTypeOverride?: InsuranceType
): Promise<FormsFetch<FormDataRequiredTemplate[]>> => {
    const skipReason = getFormsSkipReason(appointment, insuranceTypeOverride);
    if (skipReason) {
        return { status: FormsFetchStatus.SKIPPED, reason: skipReason };
    }

    const options: AxiosRequestConfig = {
        headers: { customer_id: customerId },
        params: {
            user_id: userId,
            professional_id: appointment.professional.id,
            insurance_type: insuranceTypeOverride ?? getInsuranceTypeFromAppointment(appointment),
            appointment_type_keys: joinAppointmentServicesKeys(appointment),
        },
    };
    try {
        const response: AxiosResponse<RequiredFormsResponse | undefined> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/required-templates`,
            options
        );
        return { status: FormsFetchStatus.RESOLVED, data: response.data?.templates ?? [] };
    } catch (error) {
        return toFormsFetchFailure(error, 'Failed to fetch the required forms for the appointment');
    }
};

const getSelectedFormsForAppointment = async (
    userId: string,
    customerId: string,
    appointment: DailyAppointment
): Promise<FormTemplateAppointment[] | undefined> => {
    const config = { headers: { customer_id: customerId } };
    try {
        const res: AxiosResponse<SelectedFormsResponse | undefined> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/appointment?user_id=${userId}&appointment_id=${appointment.id}`,
            config
        );
        return res?.data?.data.forms;
    } catch (error) {
        logger.debug('Failed to fetch the selected forms for the appointment', serializeApiError(error));
        return undefined;
    }
};

const getCodeByAppointment = async (
    userId: string,
    customerId: string,
    location: EnrichedLocation,
    appointment: DailyAppointment
): Promise<FormDataCode | undefined> => {
    const config: AxiosRequestConfig = {
        headers: { customer_id: customerId },
        params: {
            appointment_id: appointment.id,
            location_key: location.key,
            user_id: userId,
        },
    };
    try {
        const res = await api.get<FormDataCode>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/codes/appointment`,
            config
        );
        return res?.data;
    } catch (error) {
        logger.debug('Failed to fetch the form-data code for the appointment', {
            error: error,
            ...serializeApiError(error),
        });
        return undefined;
    }
};

export const getFormTemplates = async (
    professionalId: string,
    customerId: string
): Promise<FormsFetch<FormTemplate[]>> => {
    const config = { headers: { customer_id: customerId } };
    try {
        const res = await api.get<{ data: FormTemplate[] }>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/templates?professional_id=${professionalId}`,
            config
        );
        return { status: FormsFetchStatus.RESOLVED, data: res.data.data };
    } catch (error) {
        return toFormsFetchFailure(error, 'Failed to fetch the form templates');
    }
};

export const saveSelectedFormsForAppointment = async (
    customerId: string,
    appointmentFormsToSave: FormDataAppointmentDto
): Promise<boolean> => {
    const config = { headers: { customer_id: customerId } };
    try {
        await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/appointment`,
            appointmentFormsToSave,
            config
        );
        return true;
    } catch (error) {
        logger.debug('Failed to save the selected forms for the appointment', {
            error: error,
            ...serializeApiError(error),
        });
        return false;
    }
};

export const getSharedDocuments = async (
    userId: string,
    customerId: string
): Promise<GetDocumentsResult[] | undefined> => {
    const config = { headers: { customer_id: customerId } };
    try {
        const res: AxiosResponse<SharedFormsResponse> = await api.get(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/documents?user_id=${userId}`,
            config
        );
        return res?.data?.data;
    } catch (error) {
        logger.debug('Failed to fetch the shared documents', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

export const generateCode = async (customerId: string, codeData: FormDataCodeDto): Promise<string | undefined> => {
    const config = { headers: { customer_id: customerId } };
    try {
        const res = await api.post<FormDataCodeResult>(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.USER.FORM_DATA}/codes/generate`,
            codeData,
            config
        );
        return res?.data?.code;
    } catch (error) {
        logger.debug('Failed to generate the form-data code', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

export const getAppointmentSpecificFormsData = async (
    userId: string,
    customerId: string,
    appointment: DailyAppointment,
    location: EnrichedLocation,
    insuranceTypeOverride?: InsuranceType
): Promise<{
    appointment_forms: FormsFetch<FormDataRequiredTemplate[]>;
    selected_forms: FormTemplateAppointment[] | undefined;
    form_data_code: FormDataCode | undefined;
}> => {
    const [appointment_forms, selected_forms, form_data_code] = await Promise.all([
        getRequiredFormsForAppointment(userId, customerId, appointment, insuranceTypeOverride),
        getSelectedFormsForAppointment(userId, customerId, appointment),
        getCodeByAppointment(userId, customerId, location, appointment),
    ]);
    return { appointment_forms, selected_forms, form_data_code };
};

export const getRequiredAppointmentForms = async (
    appointment: DailyAppointment,
    selected_location: EnrichedLocation,
    insuranceTypeOverride?: InsuranceType
): Promise<RequiredForms> => {
    const user_id = getUnregisteredUserId(appointment);
    const customer_id = getCustomerId();

    const [appointment_specific_forms, professionalFormsFetch, shared_forms] = await Promise.all([
        getAppointmentSpecificFormsData(user_id, customer_id, appointment, selected_location, insuranceTypeOverride),
        getFormTemplates(appointment.professional.id, customer_id),
        getSharedDocuments(user_id, customer_id),
    ]);

    const professionalForms = getFormsFetchData(professionalFormsFetch);
    const formsResolution = getFormsResolution(
        appointment_specific_forms.appointment_forms,
        professionalFormsFetch,
        appointment_specific_forms.selected_forms
    );

    let formatted_forms: FormattedForm[] | undefined;
    if (professionalForms && formsResolution === FormsResolution.RESOLVED) {
        formatted_forms = formatRequiredForms(
            appointment_specific_forms.selected_forms,
            getFormsFetchData(appointment_specific_forms.appointment_forms),
            professionalForms,
            shared_forms,
            appointment_specific_forms.form_data_code,
            appointment
        );
    }

    return {
        professional_form_templates: professionalForms,
        selected_appointment_forms: appointment_specific_forms.selected_forms,
        formatted_forms_for_appointment: formatted_forms,
        forms_resolution: formsResolution,
        form_data_code: appointment_specific_forms.form_data_code,
    };
};
